import { useMemo, useState } from 'react'
import { ReverLoading, toast } from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import NotFound from '@/ui/pages/404.page'
import {
  useApiKeyValue,
  useApiKeys,
  useCreateApiKey,
  useRevokeApiKey
} from '@/application/manage-api-keys'
import { ApiKeysSettings } from './api-keys/api-keys-settings'
import { CreateKeyDialog } from './api-keys/create-key-dialog'
import { KeyValueDialog } from './api-keys/key-value-dialog'
import { RevokeKeyDialog } from './api-keys/revoke-key-dialog'
import { ApiKeyRow } from './api-keys/types'

type OpenDialog = 'none' | 'create' | 'value' | 'revoke'

export default function ApiKeys() {
  const { t } = useTranslation()
  const profile = useAppSelector((s) => s.userApi.getMe)
  const user = profile.response.user
  const settings = useAppSelector((s) => s.settingsApi.settings.response)
  const selectedEcommerce = useAppSelector(
    (s) => s.generalData.selectedEcommerce
  )
  const selectedGroup = useAppSelector((s) => s.generalData.selectedGroup)
  // getMe is dispatched from an effect, so on a direct load the role is not known yet. Deciding
  // before it lands would flash the 404 page at an admin refreshing this URL.
  const loadingProfile =
    profile.loading === 'idle' || profile.loading === 'pending'
  const isAdmin = user?.role === Roles.ReadWrite
  const ownGroup = user?.group ?? ''
  const isRever = ownGroup === '*'

  // Keys are group-wide, so a merchant Admin sends no group and the server answers for its own.
  // Staff have none, so the group comes from the picked group or the picked store's settings — but
  // only once those settings are for that store: they follow useEcommerceId, which lags a store
  // switch and falls back to the user's first portal, and either would list another group's keys.
  // A group without a name is shown under a label, which is no group to list.
  const pickedGroup =
    !!selectedGroup && !!user?.groups?.some((g) => g.id === selectedGroup)
      ? selectedGroup
      : ''
  const settingsMatchSelection =
    !!selectedEcommerce && settings.ecommerce_id === selectedEcommerce
  const scopeGroup = isRever
    ? pickedGroup || (settingsMatchSelection ? (settings.group ?? '') : '')
    : ownGroup

  const [dialog, setDialog] = useState<OpenDialog>('none')
  const [activeKey, setActiveKey] = useState<ApiKeyRow | undefined>()
  const [justCreated, setJustCreated] = useState(false)

  const canList = isAdmin && !!scopeGroup
  const { apiKeys, loadingApiKeys, apiKeysError, reloadApiKeys } = useApiKeys(
    isRever ? [scopeGroup] : [],
    canList
  )
  const { createKey, creating, created, clearCreated } = useCreateApiKey()
  const { readKeyValue, keyValue, reading, clearKeyValue } = useApiKeyValue()
  const { revokeKey, revoking } = useRevokeApiKey()

  const keys: ApiKeyRow[] = useMemo(
    () =>
      apiKeys.map((key) => ({
        group: key.ecommerce_group ?? '',
        name: key.name ?? '',
        scopes: key.scopes ?? [],
        createdAt: key.created_at,
        createdBy: key.created_by ?? '',
        lastUsedAt: key.last_used_at
      })),
    [apiKeys]
  )

  if (loadingProfile) return <ReverLoading />
  // The API refuses a non-admin caller regardless of what this page renders.
  if (!isAdmin) return <NotFound />

  const closeDialogs = () => {
    setDialog('none')
    setActiveKey(undefined)
    clearCreated()
    clearKeyValue()
  }

  const handleCreate = async (name: string, scopes: string[]) => {
    const response = await createKey({ group: scopeGroup, name, scopes })
    // Creating a name the group already holds returns that key's value: a read by another name.
    // Staff must not get it, so the dialog never opens for them in that case.
    if (isRever && response.existing) {
      toast({ text: t('api_keys.existing_not_shown'), variant: 'error' })
      closeDialogs()
      return
    }
    const key = response.key
    setActiveKey({
      group: key?.ecommerce_group ?? scopeGroup,
      name: key?.name ?? name,
      scopes: key?.scopes ?? scopes,
      createdAt: key?.created_at,
      createdBy: key?.created_by ?? ''
    })
    setJustCreated(true)
    setDialog('value')
  }

  const handleView = (key: ApiKeyRow) => {
    setActiveKey(key)
    setJustCreated(false)
    setDialog('value')
    readKeyValue({ group: key.group, name: key.name })
  }

  const handleRevoke = async () => {
    if (!activeKey) return
    await revokeKey({ group: activeKey.group, name: activeKey.name })
    closeDialogs()
  }

  return (
    <>
      <ApiKeysSettings
        viewer={isRever ? 'rever' : 'admin'}
        group={scopeGroup}
        keys={keys}
        loading={loadingApiKeys && canList}
        error={apiKeysError}
        onRetry={reloadApiKeys}
        onCreate={() => setDialog('create')}
        onView={handleView}
        onRevoke={(key) => {
          setActiveKey(key)
          setDialog('revoke')
        }}
      />

      <CreateKeyDialog
        isOpen={dialog === 'create'}
        group={scopeGroup}
        keys={keys}
        saving={creating}
        onClose={closeDialogs}
        onCreate={handleCreate}
        onViewExisting={handleView}
      />
      <KeyValueDialog
        isOpen={dialog === 'value'}
        apiKey={activeKey}
        value={justCreated ? created?.value : keyValue}
        loading={reading}
        justCreated={justCreated}
        existing={justCreated && !!created?.existing}
        canReadAgain={!isRever}
        onClose={closeDialogs}
      />
      <RevokeKeyDialog
        isOpen={dialog === 'revoke'}
        apiKey={activeKey}
        saving={revoking}
        onClose={closeDialogs}
        onRevoke={handleRevoke}
      />
    </>
  )
}
